/**
 * PairConfirmCard — post-pair confirm card (browser-side mirror of Android's
 * `PairConfirmCard.kt`). Shown for 7 days after pairing when the auto-name
 * pipeline produced no collision and the user hasn't tapped "Looks good" or
 * "Edit" yet.
 *
 * Headless: the host owns the visibility decision (storage gate + 7-day
 * window) and the rename API call; this renders the auto-name with two
 * actions plus an inline rename dialog. Keeping visibility out of the
 * component makes both sides cheap to test — the card in isolation, the
 * gate around the host's slot.
 *
 * Extracted from two independent copies (`apps/extension` and `apps/web`)
 * that had diverged only in i18n access and styling. See the file header on
 * `renderMessageWithName` for the one behavioural difference the merge
 * settles.
 */
import { MAX_DEVICE_NAME_LENGTH } from "@reflecto/client-core";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { trapTabKey } from "../../utils/focusTrap";
import { Button } from "../Button";
import styles from "./PairConfirmCard.module.css";

export interface PairConfirmCardLabels {
  /** aria-label for the card's <section>. */
  cardAriaLabel: string;
  /**
   * Body copy, given the device name. The name is rendered as `<strong>`
   * wherever the function places it — see `renderMessageWithName`.
   */
  cardMessage: (name: string) => string;
  cardLooksGood: string;
  cardEdit: string;
  renameTitle: string;
  renameLabel: string;
  renameFailed: string;
  renameCancel: string;
  renameSave: string;
}

export interface PairConfirmCardProps {
  /** Auto-generated device name as written to the server. */
  autoName: string;
  /** Invoked when the user accepts the suggested name. */
  onLooksGood: () => void;
  /**
   * Invoked with a new (trimmed) name when the user submits the inline
   * rename dialog. Async so the host can surface in-flight state and let the
   * dialog show `renameFailed` on rejection.
   */
  onRename: (newName: string) => Promise<void>;
  labels: PairConfirmCardLabels;
}

/**
 * Render the body copy with the device name emphasised, without the label
 * bag having to return JSX.
 *
 * The trick: call the template with a sentinel, find the sentinel in the
 * result, and splice `<strong>{name}</strong>` into its place. That works for
 * any label shape — a template literal on web, a `browser.i18n` catalog entry
 * with a `$NAME$` placeholder on the extension — because by the time the
 * function returns, the substitution has already happened either way.
 *
 * This is the web copy's approach, and adopting it fixes the extension. Its
 * version searched the *substituted* string for a literal `$NAME$`, which
 * Chrome had already replaced, so the name never actually rendered bold
 * there — a silent divergence from both web and Android, acknowledged in a
 * comment in the old extension file.
 *
 * Falls back to a flat, unemphasised render if the sentinel doesn't survive
 * the template (e.g. a locale that drops the slot).
 */
function renderMessageWithName(template: (name: string) => string, name: string) {
  // Wrapped in U+0000 NULLs — never produced by `autoNameDevice`'s tier A–C
  // pipeline (printable UA-derived tokens only), so a real device name can
  // never collide with the placeholder, however contrived.
  const SENTINEL = "\u0000PAIR_CONFIRM_NAME_SLOT\u0000";
  const rendered = template(SENTINEL);
  const idx = rendered.indexOf(SENTINEL);
  if (idx === -1) return template(name);
  return (
    <>
      {rendered.slice(0, idx)}
      <strong>{name}</strong>
      {rendered.slice(idx + SENTINEL.length)}
    </>
  );
}

export function PairConfirmCard({ autoName, onLooksGood, onRename, labels }: PairConfirmCardProps) {
  const [editing, setEditing] = useState(false);
  // Restore focus to the Edit button when the rename dialog closes (WCAG 2.1 SC 2.4.3).
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const closeDialog = () => {
    setEditing(false);
    // Defer to the next microtask so React re-renders the section before we
    // focus (a sync focus would race the commit).
    queueMicrotask(() => editButtonRef.current?.focus());
  };

  return (
    <>
      <section
        className={styles.card}
        aria-label={labels.cardAriaLabel}
        data-testid="pair-confirm-card"
      >
        <p className={styles.message}>{renderMessageWithName(labels.cardMessage, autoName)}</p>
        <div className={styles.actions}>
          <Button variant="primary" size="m" onClick={onLooksGood}>
            {labels.cardLooksGood}
          </Button>
          <Button variant="secondary" size="m" ref={editButtonRef} onClick={() => setEditing(true)}>
            {labels.cardEdit}
          </Button>
        </div>
      </section>
      {editing && (
        <PairConfirmRenameDialog
          currentName={autoName}
          onConfirm={async (newName) => {
            await onRename(newName);
            closeDialog();
          }}
          onCancel={closeDialog}
          labels={labels}
        />
      )}
    </>
  );
}

interface PairConfirmRenameDialogProps {
  currentName: string;
  onConfirm: (newName: string) => Promise<void>;
  onCancel: () => void;
  labels: PairConfirmCardLabels;
}

function PairConfirmRenameDialog({
  currentName,
  onConfirm,
  onCancel,
  labels,
}: PairConfirmRenameDialogProps) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  // Focus the input on first mount only. An inline ref callback would fire on
  // every render (each keystroke) and re-issue focus(), which can interrupt IME
  // composition and selection.
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // useId() instead of a hardcoded string so multiple dialogs on the same page
  // (hypothetical future surface composition) can't share an aria-labelledby target.
  const titleId = useId();
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !saving;

  // Tab focus trap: wrap focus within the dialog's enabled focusable children.
  // Without this, a keyboard user can Tab out of the dialog into the obscured
  // page behind the backdrop. Re-queries the DOM each Tab so disabled-state
  // changes (e.g. Save enabling once the user types) are reflected without
  // dependency tracking.
  const handleTabTrap = (e: KeyboardEvent<HTMLDivElement>) => {
    trapTabKey(dialogRef.current, e);
  };

  return (
    // role="dialog" sits on the inner visible box (not the full-screen backdrop)
    // per WAI-ARIA: the role represents the dialog's content boundary, not the
    // click-shield. The backdrop also catches Escape, Tab (focus trap), and
    // click-outside-to-dismiss — the standard close + focus-containment
    // affordances for modals.
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop catches bubbled events; the dialog role lives on the inner box.
    <div
      className={styles.dialogBackdrop}
      onKeyDown={(e) => {
        if (e.key === "Escape" && !saving) onCancel();
        else handleTabTrap(e);
      }}
      onClick={(e) => {
        // Only dismiss when the click landed on the backdrop itself, not when it
        // bubbled up from a child (e.g. the input or a button inside the dialog).
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className={styles.dialogTitle}>
          {labels.renameTitle}
        </h2>
        <label className={styles.dialogLabel}>
          <span>{labels.renameLabel}</span>
          <input
            className={styles.dialogInput}
            type="text"
            value={name}
            maxLength={MAX_DEVICE_NAME_LENGTH}
            onChange={(e) => {
              setName(e.target.value);
              setError(false);
            }}
            disabled={saving}
            ref={inputRef}
          />
        </label>
        {error && <span className={styles.dialogError}>{labels.renameFailed}</span>}
        <div className={styles.dialogActions}>
          <Button variant="secondary" size="m" onClick={onCancel} disabled={saving}>
            {labels.renameCancel}
          </Button>
          <Button
            variant="primary"
            size="m"
            loading={saving}
            disabled={!canSave}
            onClick={async () => {
              setSaving(true);
              setError(false);
              try {
                await onConfirm(trimmed);
              } catch {
                setError(true);
              } finally {
                // Always clear in-flight state, even on success — the host normally
                // unmounts this dialog after onConfirm resolves, but `finally` removes
                // the implicit dependency on that timing.
                setSaving(false);
              }
            }}
          >
            {labels.renameSave}
          </Button>
        </div>
      </div>
    </div>
  );
}
